import { createColumnHelper } from '@tanstack/react-table';
import { groupBy } from 'lodash';
import { useMemo } from 'react';

import { CopiableGuid } from '@/components/CopiableGuid';

import {
    baseCol,
    dateCol,
    excludeTradesCol,
    expanderCol,
    moneyCol,
    numberCol,
    percentageCol,
    textCol,
} from '../../../widgets/Table/columnHelpers/colDef';
import {
    CommonTradePlanEntry,
    getTradeAggregates,
    getTradeValueAsPercentOfNav,
    TradeTableProps,
} from './tradeExecutionUtils';
import { TradesTable } from './TradesTable';

interface TradePlanEntry extends CommonTradePlanEntry {
    portfolioGuid: string;
    aC?: string;
    nav?: number;
    cash?: number;
    tradeDate?: string;
    percentOfNav: number;
}

const columnHelper = createColumnHelper<TradePlanEntry>();
export const TradesByPortfolio = ({
    secondaryGrouping,
    filteredTrades,
    excludeTrades,
    viewAsset,
    viewPortfolio,
    viewTrade,
    viewAdvanced,
}: TradeTableProps) => {
    const data: TradePlanEntry[] = useMemo(() => {
        if (!filteredTrades) {
            return [];
        }

        const grouped = groupBy(filteredTrades, (trade) => trade.portfolioGuid);
        return Object.entries(grouped).map(([portfolioGuid, trades]) => {
            return {
                portfolioGuid,
                trades,
                aC: trades[0].aC,
                nav: trades[0].nav,
                cash: trades[0].cash,
                tradeDate: trades[0].tradeDate,
                ...getTradeAggregates(trades),
                percentOfNav: trades.reduce((result, trade) => result + (getTradeValueAsPercentOfNav(trade) ?? 0), 0),
            };
        });
    }, [filteredTrades]);

    const columns = [
        columnHelper.display(expanderCol()),
        columnHelper.accessor('portfolioGuid', {
            ...baseCol({ id: 'portfolioGuid' }),
            cell: ({ getValue }) => <CopiableGuid className="text-white" guid={getValue()} showBorder={false} />,
        }),
        columnHelper.accessor('aC', textCol({ id: 'aC' })),
        columnHelper.accessor('nav', moneyCol({ id: 'nav' })),
        columnHelper.accessor('cash', moneyCol({ id: 'cash' })),
        columnHelper.accessor('tradeDate', dateCol({ id: 'tradeDate' })),
        columnHelper.accessor('tradesCount', numberCol({ id: 'tradesCount', format: { fractionDigits: 0 } })),
        columnHelper.accessor('totalQuantity', numberCol({ id: 'totalQuantity', format: { fractionDigits: 0 } })),
        columnHelper.accessor('totalValue', moneyCol({ id: 'totalValue' })),
        columnHelper.accessor('percentOfNav', percentageCol({ id: 'percentOfNav' })),
        columnHelper.display(excludeTradesCol(excludeTrades)),
    ];

    return (
        <TradesTable
            data={data}
            secondaryGrouping={secondaryGrouping}
            columns={columns}
            excludeTrades={excludeTrades}
            viewAsset={viewAsset}
            viewPortfolio={viewPortfolio}
            viewTrade={viewTrade}
            viewAdvanced={viewAdvanced}
        />
    );
};
